import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../context/ProductStore';
import {
  Search, Filter, Plus, Edit2, Trash2,
  Eye, EyeOff, Package, ChevronDown, X
} from 'lucide-react';
import './ProductsList.css';

export default function ProductsList() {
  const { products, categories = [], toggleStatus, toggleStock, deleteProduct, getFullUrl } = useStore();
  const [search,    setSearch]    = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stockFilter,  setStockFilter]  = useState('all');
  const [selected,  setSelected]  = useState([]);
  const [confirm,   setConfirm]   = useState(null);
  const [page,      setPage]      = useState(1);
  const PER_PAGE = 12;

  const filtered = useMemo(() => {
    let res = [...products];
    if (search)     res = res.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.sku?.toLowerCase().includes(search.toLowerCase()));
    if (catFilter !== 'all')    res = res.filter(p => p.category === catFilter);
    if (statusFilter !== 'all') res = res.filter(p => p.status   === statusFilter);
    if (stockFilter  !== 'all') res = res.filter(p => p.stock    === stockFilter);
    return res;
  }, [products, search, catFilter, statusFilter, stockFilter]);

  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  const toggleSelect  = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll     = ()   => setSelected(s => s.length === paginated.length ? [] : paginated.map(p => p.id));
  const handleDelete  = (id) => { setConfirm(id); };
  const confirmDelete = ()   => { if (confirm) { deleteProduct(confirm); setConfirm(null); setSelected(s => s.filter(x => x !== confirm)); } };

  const clearFilters = () => { setSearch(''); setCatFilter('all'); setStatusFilter('all'); setStockFilter('all'); setPage(1); };
  const hasFilters = search || catFilter !== 'all' || statusFilter !== 'all' || stockFilter !== 'all';

  return (
    <div className="products-list-page">
      {/* Header */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Products</h1>
          <p className="admin-page-sub">{products.length} total products · {filtered.length} shown</p>
        </div>
        <Link to="/admin/add" className="admin-btn admin-btn-primary">
          <Plus size={16} /> Add Product
        </Link>
      </div>

      {/* Filters */}
      <div className="admin-card pl-filters">
        <div className="pl-search-wrap">
          <Search size={15} className="pl-search-icon" />
          <input
            className="pl-search-input"
            placeholder="Search by name or SKU..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && <button className="pl-clear-btn" onClick={() => setSearch('')}><X size={14} /></button>}
        </div>

        <div className="pl-filter-group">
          <div className="pf-select-wrap pl-select">
            <Filter size={13} className="pl-filter-icon" />
            <select className="pl-select-input" value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}>
              <option value="all">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
            </select>
            <ChevronDown size={12} className="pl-chevron" />
          </div>

          <div className="pf-select-wrap pl-select">
            <select className="pl-select-input" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
            <ChevronDown size={12} className="pl-chevron" />
          </div>

          <div className="pf-select-wrap pl-select">
            <select className="pl-select-input" value={stockFilter} onChange={e => { setStockFilter(e.target.value); setPage(1); }}>
              <option value="all">All Stock</option>
              <option value="instock">In Stock</option>
              <option value="outofstock">Out of Stock</option>
            </select>
            <ChevronDown size={12} className="pl-chevron" />
          </div>

          {hasFilters && (
            <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={clearFilters}>
              <X size={13} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="pl-bulk-bar">
          <span className="pl-bulk-count">{selected.length} selected</span>
          <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => { selected.forEach(id => deleteProduct(id)); setSelected([]); }}>
            <Trash2 size={13} /> Delete Selected
          </button>
          <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setSelected([])}>
            Cancel
          </button>
        </div>
      )}

      {/* Table */}
      <div className="admin-card pl-table-card">
        {filtered.length === 0 ? (
          <div className="pl-empty">
            <Package size={48} />
            <h3>No products found</h3>
            <p>Try adjusting your search or filter, or add a new product.</p>
            <Link to="/admin/add" className="admin-btn admin-btn-primary">Add Product</Link>
          </div>
        ) : (
          <>
            <div className="pl-table-wrap">
              <table className="pl-table">
                <thead>
                  <tr>
                    <th>
                      <input type="checkbox" checked={selected.length === paginated.length && paginated.length > 0} onChange={toggleAll} className="pl-checkbox" />
                    </th>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Status</th>
                    <th>Date Added</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(p => {
                    const discount = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;
                    return (
                      <tr key={p.id} className={selected.includes(p.id) ? 'selected' : ''}>
                        <td>
                          <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} className="pl-checkbox" />
                        </td>
                        <td>
                          <div className="pl-product-cell">
                            <img
                              src={getFullUrl(p.images?.[0]?.url || p.image)}
                              alt={p.name}
                              className="pl-img"
                              onError={e => e.target.src='https://via.placeholder.com/48x48/f3f4f6/9ca3af?text=P'}
                            />
                            <div>
                              <p className="pl-name">{p.name}</p>
                              <p className="pl-weight">{p.weight}</p>
                              {discount > 0 && <span className="pl-discount-badge">{discount}% OFF</span>}
                            </div>
                          </div>
                        </td>
                        <td><code className="pl-sku">{p.sku || '—'}</code></td>
                        <td>
                          <span className="pl-cat-badge">
                            {categories.find(c => c.id === p.category)?.emoji} {p.category}
                          </span>
                        </td>
                        <td>
                          <div>
                            <span className="pl-price">₹{p.price}</span>
                            {p.mrp > p.price && <span className="pl-mrp">₹{p.mrp}</span>}
                          </div>
                        </td>
                        <td>
                          <button
                            className={`status-badge status-${p.stock} pl-toggle-badge`}
                            onClick={() => toggleStock(p.id)}
                            title="Click to toggle"
                          >
                            {p.stock === 'instock' ? '📦 In Stock' : '❌ Out of Stock'}
                          </button>
                        </td>
                        <td>
                          <button
                            className={`status-badge status-${p.status} pl-toggle-badge`}
                            onClick={() => toggleStatus(p.id)}
                            title="Click to toggle"
                          >
                            {p.status === 'published' ? <><Eye size={11} /> Published</> : <><EyeOff size={11} /> Draft</>}
                          </button>
                        </td>
                        <td><span className="pl-date">{p.dateAdded || '—'}</span></td>
                        <td>
                          <div className="pl-actions">
                            <Link to={`/admin/edit/${p.id}`} className="admin-btn admin-btn-secondary admin-btn-icon" title="Edit">
                              <Edit2 size={14} />
                            </Link>
                            <a href={`/product/${p.slug}`} target="_blank" rel="noreferrer" className="admin-btn admin-btn-secondary admin-btn-icon" title="View">
                              <Eye size={14} />
                            </a>
                            <button className="admin-btn admin-btn-danger admin-btn-icon" title="Delete" onClick={() => handleDelete(p.id)}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pl-pagination">
                <span className="pl-pag-info">Showing {(page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE, filtered.length)} of {filtered.length}</span>
                <div className="pl-pag-btns">
                  <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={page === 1} onClick={() => setPage(p => p-1)}>← Prev</button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button key={i} className={`admin-btn admin-btn-sm ${page === i+1 ? 'admin-btn-primary' : 'admin-btn-secondary'}`} onClick={() => setPage(i+1)}>
                      {i+1}
                    </button>
                  ))}
                  <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={page === totalPages} onClick={() => setPage(p => p+1)}>Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete confirm modal */}
      {confirm && (
        <div className="pl-modal-overlay">
          <div className="pl-modal">
            <div className="pl-modal-icon"><Trash2 size={28} /></div>
            <h3>Delete Product?</h3>
            <p>This action cannot be undone. The product will be permanently removed.</p>
            <div className="pl-modal-actions">
              <button className="admin-btn admin-btn-danger" onClick={confirmDelete}>Yes, Delete</button>
              <button className="admin-btn admin-btn-secondary" onClick={() => setConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
